/**
 * @file ItemAccountsPanel.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-30
 * @modified 2026-10-02
 * @description Read-only "Accounts & VAT" for one inventory item: the sales and
 *              purchase side each with its description, account (code + name)
 *              and VAT, plus the COGS account when there is one. A tracked
 *              item's purchase account is its inventory account (utils/itemAccounts).
 *
 *              For the screens that open a product in their OWN modal rather
 *              than InventoryItemModal — Minerals, Milk Powder, blend raw
 *              materials — which showed the code, name and cost and nothing of
 *              how the item posts to the ledger. Fetches the item by id so the
 *              caller need not carry these fields in its own payload; renders
 *              nothing until an item is chosen.
 */

import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { getTaxTypeLabel } from '../utils/taxTypes';
import { ACCOUNT_NAMES } from '../utils/accountCodes';
import './TableStyles.css';
import './ItemAccountsPanel.css';

function AccountValue({ code }) {
  if (!code) return <span className="iap-muted">—</span>;
  return (
    <span className="iap-account">
      <span className="account-badge-inline">{code}</span>
      {ACCOUNT_NAMES[code] || ''}
    </span>
  );
}

function Side({ title, active, description, account, taxType, accountLabel = 'Account' }) {
  return (
    <div className={`iap-side${active ? '' : ' is-off'}`}>
      <div className="iap-side-title">
        {title}
        {!active && <span className="iap-off-note">not ticked in Xero</span>}
      </div>
      <dl>
        <dt>Description</dt>
        <dd>{description || <span className="iap-muted">—</span>}</dd>
        <dt>{accountLabel}</dt>
        <dd><AccountValue code={account} /></dd>
        <dt>VAT</dt>
        <dd>
          {taxType
            ? <>{getTaxTypeLabel(taxType)} <span className="iap-muted">{taxType}</span></>
            : <span className="iap-muted">—</span>}
        </dd>
      </dl>
    </div>
  );
}

function ItemAccountsPanel({ itemId }) {
  const [item, setItem] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setItem(null);
    setError('');
    if (!itemId) return undefined;
    let alive = true;
    fetch(`${API_URL}/api/inventory/${itemId}`, { credentials: 'include' })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Could not load the item');
        if (alive) setItem(data.item || null);
      })
      .catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [itemId]);

  if (!itemId) return null;

  return (
    <div className="item-accounts-panel">
      <div className="iap-heading">Accounts &amp; VAT</div>
      {error ? (
        <p className="iap-muted">{error}</p>
      ) : !item ? (
        <p className="iap-muted">Loading…</p>
      ) : (
        <>
          <div className="iap-sides">
            <Side
              title="Sales"
              active={item.is_sold !== false}
              description={item.description}
              account={item.sales_account_code}
              taxType={item.sales_tax_type}
            />
            {/* A tracked item posts purchases to its inventory account, not a
                purchase account (utils/itemAccounts); COGS goes underneath. */}
            <Side
              title="Purchases"
              active={item.is_purchased !== false}
              description={item.purchase_description}
              accountLabel={item.is_tracked_as_inventory ? 'Inventory account' : 'Account'}
              account={item.is_tracked_as_inventory ? item.inventory_asset_account_code : item.purchase_account_code}
              taxType={item.purchase_tax_type}
            />
          </div>
          {item.cogs_account_code && (
            <div className="iap-cogs">
              <span className="iap-cogs-label">COGS account</span>
              <AccountValue code={item.cogs_account_code} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ItemAccountsPanel;
